import type { Plan } from './constants.js';
import type { ScopeId } from './scopes.js';
import type { InferValue, ValueSpec } from './value-spec.js';

/** Uwaga przypięta do konkretnej wartości ustawienia. */
export type ValueNote = {
  /** `blocking` zatrzymuje zapis presetu, `warn` tylko ostrzega. */
  readonly severity: 'info' | 'warn' | 'blocking';
  readonly textPl: string;
};

type CommonEntry<S extends ValueSpec> = {
  readonly value: S;
  /** Opis dla pracownika składającego preset. */
  readonly staff: { readonly label: string; readonly hint: string };
  /** Zdanie prostym językiem dla klienta. Bez żargonu. */
  readonly customer: { readonly changePl: (value: InferValue<S>) => string };
  /**
   * DORADCZE. Autorytatywnym źródłem dostępności jest pole `editable`
   * zwracane przez `GET /zones/{id}/settings/{setting_id}`, bo uprawnienia
   * planów zmieniają się, istnieją plany resellerskie i legacy, a narzędzie,
   * które odmawia zmiany akceptowanej przez API, to zgłoszenie do supportu.
   * `plans` służy wyłącznie do wyszarzenia pola w formularzu i do ładniejszego
   * komunikatu po polsku.
   */
  readonly plans: { readonly min: Plan; readonly max?: Plan };
  readonly scopes: readonly ScopeId[];
  readonly deprecated?: { readonly sincePl: string };
  /** Klucz to `String(wartość)`. */
  readonly valueNotes?: Readonly<Record<string, ValueNote>>;
  /** Zarezerwowane pod diff w Etapie 4 — nieużywane w Etapie 1. */
  readonly dependsOn?: readonly string[];
  readonly recommended?: InferValue<S>;
};

/** Ustawienie strefy: `GET|PATCH /zones/{id}/settings/{settingId}`. */
export function zoneSetting<const S extends ValueSpec>(
  entry: CommonEntry<S> & { readonly settingId: string },
) {
  return { kind: 'zone-setting' as const, ...entry };
}

/**
 * Konfiguracja botów: `GET|PUT /zones/{id}/bot_management`.
 * PUT nadpisuje CAŁY obiekt konfiguracji — obowiązuje read-modify-write.
 */
export function botConfig<const S extends ValueSpec>(
  entry: CommonEntry<S> & { readonly field: string },
) {
  return { kind: 'bot-config' as const, ...entry };
}

/**
 * Wdrożenie managed rulesetu jako reguły `execute` w fazie
 * `http_request_firewall_managed`. Na poziomie strefy każdy managed ruleset
 * można wdrożyć TYLKO RAZ.
 */
export function managedRuleset<const S extends ValueSpec>(
  entry: CommonEntry<S> & {
    readonly rulesetId: string;
    readonly phase: string;
    readonly refSlug: string;
  },
) {
  return { kind: 'managed-ruleset' as const, ...entry };
}

export type AnyCatalogEntry =
  | ReturnType<typeof zoneSetting<ValueSpec>>
  | ReturnType<typeof botConfig<ValueSpec>>
  | ReturnType<typeof managedRuleset<ValueSpec>>;

export type CatalogKind = AnyCatalogEntry['kind'];
